/**
 * Centralized Branding Configuration
 *
 * Edit this single file to change ALL branding across the entire dashboard.
 * Colors, logos, and company name are all controlled from here.
 *
 * After editing, the entire app will reflect the new branding automatically.
 */

export interface BrandingConfig {
  company: {
    name: string;
    tagline?: string;
    copyright?: string;
  };
  logos: {
    primary: string;      // Main logo (e.g., "/logo.png")
    wordmark: string;     // Text/wordmark logo (e.g., "/wordmark.svg")
    favicon?: string;     // Optional favicon path
  };
  colors: {
    primary: string;      // Main brand color
    secondary: string;    // Secondary/gradient start color
    tertiary: string;     // Tertiary/gradient end color
    accent: string;       // Accent/highlight color
    ring: string;         // Focus ring color
    gradient: {
      start: string;
      end: string;
      angle: number;      // Gradient angle in degrees
    };
    chart: string[];      // Chart color palette (5+ colors recommended)
    device?: {
      ios: string;        // Color for iOS line in device breakdown charts
      android: string;    // Color for Android line in device breakdown charts
    };
    gender?: {
      male: string;       // Color for male in gender breakdown charts
      female: string;     // Color for female in gender breakdown charts
    };
    bar?: string;         // Performance bar color (appears black/dark)
    // Page background colors (light and dark mode)
    background?: {
      light: string;      // Background color for light mode (default: #f5f5f5)
      dark: string;       // Background color for dark mode (default: #0a0a0a)
    };
    // Card/panel background colors
    card?: {
      light: string;      // Card background for light mode (default: #ffffff)
      dark: string;       // Card background for dark mode (default: #171717)
    };
  };
  email: {
    headerGradientStart: string;
    headerGradientEnd: string;
    buttonBackground: string;
    accentText: string;
  };
}

/**
 * ============================================
 * EDIT BELOW TO CHANGE BRANDING
 * ============================================
 */
export const branding: BrandingConfig = {
  company: {
    name: "Ascend",
    tagline: "Sales Dashboard",
    copyright: "Ascend",
  },

  logos: {
    primary: "/logo.png",
    wordmark: "/logo.png",    // Using same logo (no separate wordmark)
    favicon: "/favicon.ico",
  },

  colors: {
    // Main brand colors - Gold accent on black/gray
    primary: "#C49A3C",      // Gold - primary actions, active states
    secondary: "#222222",    // Dark gray - input/element backgrounds
    tertiary: "#A8842A",     // Dark gold - gradient end
    accent: "#2a2a2a",       // Gray - highlights, badges
    ring: "#C49A3C",         // Gold - focus rings

    // Gradient definition (gold for buttons)
    gradient: {
      start: "#D4AF5C",      // Light gold
      end: "#A8842A",        // Dark gold
      angle: 135,
    },

    // Chart color palette — gold theme
    chart: [
      "#C49A3C",  // gold       — primary series
      "#D4AF5C",  // light gold — secondary series
      "#A8842A",  // dark gold  — tertiary
      "#E8C97A",  // pale gold  — fourth series
      "#8B6E22",  // deep gold  — fifth series
      "#F5E6C4",  // cream      — sixth series
      "#BF9030",  // mid gold   — seventh
      "#6B5219",  // richest    — eighth
    ],

    // Device breakdown chart colors
    device: {
      ios:     "#C49A3C",  // gold   — iOS
      android: "#D4AF5C",  // light gold — Android
    },

    // Gender breakdown chart colors
    gender: {
      male:   "#C49A3C",  // gold        — Male
      female: "#E8C97A",  // pale gold   — Female
    },

    // Performance bar color
    bar: "#111111",       // near-black

    // Page background colors
    background: {
      light: "#f5f5f5",   // Light gray for light mode
      dark: "#0a0a0a",    // Pure black for dark mode
    },

    // Card/panel background colors
    card: {
      light: "#ffffff",   // White for light mode
      dark: "#141414",    // Neutral dark gray for dark mode
    },
  },

  email: {
    headerGradientStart: "#D4AF5C",
    headerGradientEnd: "#A8842A",
    buttonBackground: "#C49A3C",
    accentText: "#C49A3C",
  },
};

// ============================================
// HELPER FUNCTIONS (do not edit)
// ============================================

/**
 * Generate CSS gradient string from config
 */
export function getGradientCSS(
  gradient: BrandingConfig["colors"]["gradient"] = branding.colors.gradient
): string {
  return `linear-gradient(${gradient.angle}deg, ${gradient.start} 0%, ${gradient.end} 100%)`;
}

/**
 * Generate Tailwind gradient classes
 * Returns: "from-[#11b2f8] to-[#504df9]"
 */
export function getGradientTailwind(
  gradient: BrandingConfig["colors"]["gradient"] = branding.colors.gradient
): string {
  return `from-[${gradient.start}] to-[${gradient.end}]`;
}

/**
 * Get chart color by index (wraps around if index exceeds array length)
 */
export function getChartColor(index: number): string {
  const chart = branding.colors.chart;
  if (chart.length === 0) return branding.colors.primary;
  return chart[index % chart.length] ?? branding.colors.primary;
}

/**
 * Inject all branding CSS variables into the document root
 * Called automatically by ThemeProvider
 */
export function injectBrandingCSS(): void {
  if (typeof document === "undefined") return;

  const root = document.documentElement;
  const { colors } = branding;

  // Core brand colors
  root.style.setProperty("--brand-primary", colors.primary);
  root.style.setProperty("--brand-secondary", colors.secondary);
  root.style.setProperty("--brand-tertiary", colors.tertiary);
  root.style.setProperty("--brand-accent", colors.accent);
  root.style.setProperty("--brand-ring", colors.ring);

  // Gradient colors
  root.style.setProperty("--brand-gradient-start", colors.gradient.start);
  root.style.setProperty("--brand-gradient-end", colors.gradient.end);

  // Chart colors (injected after theme detection below)

  // Background colors (with defaults)
  const bgLight = colors.background?.light ?? "#f5f5f5";
  const bgDark = colors.background?.dark ?? "#0a0a0a";
  const cardLight = colors.card?.light ?? "#ffffff";
  const cardDark = colors.card?.dark ?? "#171717";

  root.style.setProperty("--brand-bg-light", bgLight);
  root.style.setProperty("--brand-bg-dark", bgDark);
  root.style.setProperty("--brand-card-light", cardLight);
  root.style.setProperty("--brand-card-dark", cardDark);

  // Also update the semantic color variables
  root.style.setProperty("--primary", colors.primary);
  root.style.setProperty("--ring", colors.ring);

  // Update background based on current theme
  const isDark = root.classList.contains("dark");
  const isCrimson = root.classList.contains("crimson");
  const isNoir = root.classList.contains("noir");
  // Set background and card based on current theme
  // Must use inline style (not CSS class) to win the specificity battle
  if (isNoir) {
    root.style.setProperty("--brand-primary", "#ffffff");
    root.style.setProperty("--brand-secondary", "#cccccc");
    root.style.setProperty("--brand-tertiary", "#555555");
    root.style.setProperty("--brand-accent", "#333333");
    root.style.setProperty("--brand-ring", "#ffffff");
    root.style.setProperty("--brand-gradient-start", "#cccccc");
    root.style.setProperty("--brand-gradient-end", "#444444");
    root.style.setProperty("--primary", "#ffffff");
    root.style.setProperty("--ring", "#ffffff");
    ["#ffffff", "#cccccc", "#999999", "#666666", "#444444"].forEach((color, i) => {
      root.style.setProperty(`--chart-${i + 1}`, color);
    });
    root.style.setProperty("--background", "#0a0a0a");
    root.style.setProperty("--card", "#111111");
    root.style.removeProperty("--foreground");
    root.style.removeProperty("--card-foreground");
  } else if (isCrimson) {
    root.style.setProperty("--background", "transparent");
    root.style.setProperty("--card", "rgba(255, 255, 255, 0.93)");
    root.style.setProperty("--foreground", "#ffffff");
    root.style.setProperty("--card-foreground", "#1a0404");
    root.style.setProperty("--popover", "rgba(255, 255, 255, 0.97)");
    root.style.setProperty("--popover-foreground", "#1a0404");
    root.style.setProperty("--border", "rgba(255, 255, 255, 0.28)");
    root.style.setProperty("--muted-foreground", "rgba(255,255,255,0.75)");
  } else if (isDark) {
    root.style.setProperty("--background", "transparent");
    root.style.setProperty("--card", cardDark);
    // Green chart palette for dark mode
    const darkChartColors = ["#C49A3C", "#D4AF5C", "#E8C97A", "#A8842A", "#F5E6C4"];
    darkChartColors.forEach((color, i) => {
      root.style.setProperty(`--chart-${i + 1}`, color);
    });
  } else {
    // Default chart colors from branding config
    colors.chart.forEach((color, i) => {
      root.style.setProperty(`--chart-${i + 1}`, color);
    });
    root.style.setProperty("--background", bgLight);
    root.style.setProperty("--card", cardLight);
    root.style.removeProperty("--foreground");
    root.style.removeProperty("--card-foreground");
    root.style.removeProperty("--popover");
    root.style.removeProperty("--popover-foreground");
    root.style.removeProperty("--border");
    root.style.removeProperty("--muted-foreground");
  }
}

/**
 * Get the MongoDB collection prefix based on company name
 * Converts "SalesGrid" to "salesgrid_", "My Company" to "my_company_"
 */
export function getCollectionPrefix(): string {
  return branding.company.name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "") + "_";
}

/**
 * Get a prefixed collection name
 * Example: getCollectionName("users") returns "salesgrid_users"
 */
export function getCollectionName(baseName: string): string {
  return getCollectionPrefix() + baseName;
}

/**
 * Get a prefixed model name (PascalCase)
 * Example: getModelName("User") returns "SalesGrid_User"
 */
export function getModelName(baseName: string): string {
  const prefix = branding.company.name.replace(/[^a-zA-Z0-9]+/g, "");
  return `${prefix}_${baseName}`;
}
